import $ from 'jquery';
import { __ } from '@wordpress/i18n';

interface GCOAIFieldArgs {
	formId: number;
	fieldId: number;
	triggerMethod: string;
	nonce: string;
	entryNonce?: string;
}

class GCOAIField {
	public formId: number;
	public fieldId: number;
	public triggerMethod: string;

	protected nonce: string;
	protected entryNonce?: string;

	public $form: JQuery<HTMLFormElement>;
	public $field: JQuery<HTMLElement>;
	public $output: JQuery;
	public $buttonTrigger: JQuery;
	public $buttonRegenerate: JQuery;

	public $inputResponse: JQuery<HTMLInputElement>;

	public loadingText: string;
	public waitingForInputText: string;

	constructor(args: GCOAIFieldArgs) {
		this.formId = args.formId;
		this.fieldId = args.fieldId;
		this.triggerMethod = args.triggerMethod;
		this.nonce = args.nonce;
		this.entryNonce = args.entryNonce;

		/*
		 * Locate the field/form via the rendered data-gcoai-* attributes.
		 * This survives plugins that rewrite GF's form/field IDs in the HTML.
		 */
		const $fieldContainer = $(
			`[data-gcoai-form-id="${this.formId}"][data-gcoai-field-id="${this.fieldId}"]`
		).first();

		const $field = $fieldContainer.closest('.gfield');
		this.$field = ($field.length ? $field : $fieldContainer) as JQuery<HTMLElement>;

		const $form = $fieldContainer.closest('form');
		this.$form = ($form.length
			? $form
			: $('#gform_' + this.formId)) as JQuery<HTMLFormElement>;

		this.$output = $fieldContainer.find('.gcoai-output');

		this.$buttonTrigger = $fieldContainer.find('.gcoai-trigger');
		this.$buttonRegenerate = $fieldContainer.find('.gcoai-regenerate');

		this.$inputResponse =
			$fieldContainer.find<HTMLInputElement>('.gcoai-response');

		this.loadingText = __('Loading…', 'gc-openai');
		this.waitingForInputText = __('Waiting for input…', 'gc-openai');
	}

	public init() {
		// If the response is already present (e.g. multi-page), rehydrate.
		if (this.$inputResponse.val()) {
			this.rehydrate();
		}

		this.bind();
	}

	public rehydrate() {
		throw new Error('Method not implemented.');
	}

	public bind() {
		this.$buttonRegenerate.on('click', () => {
			this.generate();
		});

		switch (this.triggerMethod) {
			case 'load':
				if (!this.$inputResponse.val() && !this.isFieldHidden()) {
					this.generate();
				}
				break;

			case 'button':
				this.$buttonTrigger.on('click', () => {
					this.generate();
				});
				break;
		}
	}

	public isFieldHidden() {
		return (
			window.gformIsHidden(this.$field) ||
			(this.$field.closest('.gform_page').length &&
				this.$field.closest('.gform_page').is(':hidden'))
		);
	}

	public generate() {
		throw new Error('Method not implemented.');
	}

	public addPlaceholder(text?: string) {
		const $placeholder = $(`<div class="gcoai-placeholder">${text}</div>`);
		this.$output.html($placeholder[0]);
	}

	public toggleSubmitButton(disabled: boolean): void {
		/**
		 * Disable toggling of form navigation when data is streaming.
		 *
		 * @param bool disabled Return true to disable form navigation toggling. Defaults to false.
		 */
		if (
			window.gform.applyFilters(
				'gcoai_disable_form_navigation_toggling',
				false
			)
		) {
			return;
		}

		const formClass = 'gcoai-navigation-disabled';

		// Disable form submission while XHRs are active
		if (disabled) {
			this.$form.addClass(formClass).on('submit.gcoai', (e: Event) => {
				e.preventDefault();
				return false;
			});
		} else {
			this.$form.off('submit.gcoai').removeClass(formClass);
		}

		this.getSubmitButton().prop('disabled', disabled);
	}

	public disableSubmitButton() {
		this.toggleSubmitButton(true);
	}

	public enableSubmitButton() {
		this.toggleSubmitButton(false);
	}

	public getSubmitButton = (): JQuery => {
		const $submitButton = this.$form
			.find('.gform_footer, .gform_page_footer, .gfield--type-submit')
			.find('input[type="submit"], input[type="button"]');

		/**
		 * Filter the submit button element that is used for enabling/disabling the button while GC OpenAI is
		 * streaming results.
		 *
		 * @param {JQuery} $submitButton The submit button element.
		 * @param {JQuery} $form         The form element.
		 *
		 * @since 1.2.55
		 */
		return window.gform.applyFilters(
			'gcoai_submit_button',
			$submitButton,
			this.$form
		);
	};
}

export default GCOAIField;
